import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AboutYouSteps } from '../../../../../../enums/about-you-steps.enum';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { StepperContextService } from '../../../../../../services/stepper-context.service';
import { StepperAnswersService } from '../../../../../../services/stepper-answers.service';
import { SubmitSteps } from '../../../../../../enums/submit-steps.enum';
import { internationalPhoneValidator } from '../../../../../../shared/validators/international-phone-validator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfirmationDialogComponent } from '../../../../../../../../shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { StepperLoanAppService } from '../../../../../../services/stepper-loan-app.service';
import { IncomeAndAssetsSteps } from '../../../../../../enums/income-and-assets-steps.enum';
import { OtherIncomeSourceOptions } from '../../../income-and-assets/steps/other-income-source/other-income-source.component';
import { StepSection } from '../../../../../../enums/step-section.enum';
import { IncomeSourceOptions } from '../../../income-and-assets/steps/income-source/income-source.component';
import { InvestmentExtraIncomeOptions } from '../../../income-and-assets/steps/investment-extra-income/investment-extra-income.component';

export enum ConfirmationInputs {
  Phone = 'Phone',
}

export class BusinessCheckbox {
  title = 'Business Checkbox'
  label = 'I consent to do business electronically'
  link = {
    "key": "consentToBusiness",
    "label": "View terms"
  }
}

export class MessageCheckbox {
  title = 'Message Checkbox'
  label = 'I consent to receive messages from my lender for status updates on my loan'
  link = {
    "key": "consentToMessages",
    "label": "View terms"
  }
}

@Component({
  selector: 'lgv-confirmation',
  standalone: true,
  imports: [CommonModule,
    MatDividerModule, MatInputModule,
    MatButtonModule, MatCheckboxModule,
    MatProgressSpinnerModule, ReactiveFormsModule
  ],
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConfirmationComponent {

  businessCheckbox = new BusinessCheckbox
  messageCheckbox = new MessageCheckbox
  ConfirmationInputs = ConfirmationInputs
  isLoadingSubmit = false
  formGroup = this.fb.group({
    [ConfirmationInputs.Phone]: ['', [Validators.required, internationalPhoneValidator('US'), Validators.pattern(/^\+?[0-9]*$/)]],
    [this.businessCheckbox.title]: [false, [Validators.required]],
    [this.messageCheckbox.title]: [false, [Validators.required]]
  });
  firstName = this.answerService.getAnswer(AboutYouSteps.BorrowerPersonalInfo)['First Name'] ?? ''

  @Output() stepChange = new EventEmitter<any>();
  @Output() sectionChange = new EventEmitter<any>();

  constructor(
    public contextService: StepperContextService,
    public answerService: StepperAnswersService,
    public loanAppService: StepperLoanAppService,
    private cdRef: ChangeDetectorRef,
    private fb: FormBuilder,
    public dialog: MatDialog,
  ) { }

  ngAfterViewInit() {
    let currentAnswers = this.answerService.getAnswer(SubmitSteps.Confirmation)
    if (currentAnswers && Object.keys(currentAnswers).length > 0) {
      this.formGroup.setValue({
        [ConfirmationInputs.Phone]: currentAnswers[ConfirmationInputs.Phone] as string ?? '',
      });
    }
  }

  onNext() {
    this.answerService.setAnswer(SubmitSteps.Confirmation,
      {
        [ConfirmationInputs.Phone]: this.formGroup.get(ConfirmationInputs.Phone).value,
        [this.businessCheckbox.title]: this.formGroup.get(this.businessCheckbox.title).value,
        [this.messageCheckbox.title]: this.formGroup.get(this.messageCheckbox.title).value
      })
    this.answerService.getAnswer(SubmitSteps.Confirmation)
    this.submit()
  }

  onBack() {
    if (!this.formGroup.invalid) {
      this.answerService.setAnswer(SubmitSteps.Confirmation,
        {
          [ConfirmationInputs.Phone]: this.formGroup.get(ConfirmationInputs.Phone).value,
          [this.businessCheckbox.title]: this.formGroup.get(this.businessCheckbox.title).value,
          [this.messageCheckbox.title]: this.formGroup.get(this.messageCheckbox.title).value
        })
    }
    const otherIncomeSource = this.answerService.getAnswer(IncomeAndAssetsSteps.OtherIncomeSource) ?? [];
    const incomeSource = this.answerService.getAnswer(IncomeAndAssetsSteps.IncomeSource) ?? [];

    if (otherIncomeSource.includes(OtherIncomeSourceOptions.SupportIncome)) {
      this.goToSection(StepSection.IncomeAndAssets, IncomeAndAssetsSteps.OtherSupportIncome);
    } else if (otherIncomeSource.includes(OtherIncomeSourceOptions.BankStatements)) {
      this.goToSection(StepSection.IncomeAndAssets, IncomeAndAssetsSteps.OtherBankStatements);
    } else if (incomeSource.includes(IncomeSourceOptions.OtherIncome)) {
      this.goToSection(StepSection.IncomeAndAssets, IncomeAndAssetsSteps.OtherIncomeSource);
    } else if (this.answerService.getAnswer(IncomeAndAssetsSteps.InvestmentExtraIncome) === InvestmentExtraIncomeOptions.No){
      this.goToSection(StepSection.IncomeAndAssets, IncomeAndAssetsSteps.InvestmentExtraIncome);
    } else {
      this.goToSection(StepSection.IncomeAndAssets, IncomeAndAssetsSteps.IncomeSource);
    }
  }

  goToSection(section, step) {
    this.sectionChange.emit([section, step]);
  }

  goToStep(step) {
    this.stepChange.emit(step);
  }

  isNextDisabled() {
    return this.formGroup.invalid || this.isLoadingSubmit
  }

  openViewTermsModal(key: string) {
    let confirmationText = '';
    switch (key) {
      case 'consentToBusiness':
        confirmationText = `<h2>Your Consent To Do Business Electronically (the eDisclosure Agreement)</h2>
                    <p>To use electronic signatures and receive documents electronically in connection with your use of this platform, you must read and consent to the terms outlined in this document, which require your ability to access and retain electronic documents.<\/p><p>This electronic consent, if you provide it, applies to your use of this platform on any access device, including a desktop, laptop, tablet, mobile, or other electronic device, and to any document, including loan documents, disclosures, records, payoff demands, and mortgage statements (\u201CLoan Documents\u201D), that we provide to you in electronic form. If you provide electronic consent, we will be able to provide Loan Documents to you within this platform, in other portals, and\/or through other methods we may use for delivery of Loan Documents. With Your consent, You will also be able to sign and authorize these Loan Documents electronically, rather than on paper.<\/p><p>Before We can engage in this transaction electronically, it is important that You understand Your rights and responsibilities. Please read the following and affirm Your consent to conduct business with Us electronically. For purposes of this eDisclosure Agreement, \"eDisclosures\" means the Loan Documents related to this transaction that are provided electronically, \"You\" and \"Your\" mean the borrower(s) under the applicable loan to which such Loan Documents apply, and \"We\", \"Our\" and \"Us\" mean the applicable mortgage broker(s), loan processor(s), or mortgage banker(s) with whom You are transacting business for such loan(s).<\/p><p><u>YOUR CONSENT<\/u><\/p><ul><li>Your consent to participate in this transaction electronically will apply to all Loan Documents for the applicable loans for which You are applying. If You provide Your consent by checking the \"I agree to consent...\" checkbox at the bottom of the page, We will conduct this transaction electronically, instead of providing You with the Loan Documents in paper form.<\/li><li>If a document related to Your loan is not available in electronic form, a paper copy will be provided to You free of charge.<\/li><li>Conducting this transaction electronically is an option. If You choose not to accept receipt of eDisclosures, paper Loan Documents will be mailed to You.<\/li><li>If You do not consent to receive these Loan Documents electronically, You will be provided with copies of the Loan Documents in paper form. Additionally: You will not be required to pay a fee for receiving paper copies of the Loan Documents.<\/li><\/ul><p><u>WITHDRAWAL OF CONSENT<\/u><\/p><ul><li>You have the right to withdraw Your consent at any time. By declining or revoking Your consent to receive eDisclosures, We will provide You with the Loan Documents in paper form.<\/li><li>If You originally consent to receive eDisclosures, but later decide to withdraw Your consent, You can do so by notifying Us via telephone or mail.<\/li><li>If You originally consent to receive eDisclosures, but later withdraw Your consent: You will not be required to pay a fee for withdrawing consent and receiving paper copies of the Loan Documents.<\/li><\/ul><p><u>OBTAINING PAPER COPIES<\/u><\/p><ul><li>After Your consent is given, You may request from Us paper copies of Your Loan Documents. Please send this request to Us via telephone or mail.<\/li><li>If You request paper copies of the Loan Documents: You will not be required to pay a fee for receiving paper copies of the Loan Documents.<\/li><\/ul><p><u>SYSTEM REQUIREMENTS<\/u><\/p><ul><li>In order to receive eDisclosures, You must have a computer with Internet access and an Internet email account and address; an Internet browser using 128-bit encryption or higher, Adobe Acrobat 7.0 or higher, SSL encryption, and access to a printer or the ability to download information in order to keep copies of Your eDisclosures for Your records.<\/li><li>If the software or hardware requirements change in the future, and You are unable to continue receiving eDisclosures, paper copies of such Loan Documents will be mailed to You once You notify Us that You are no longer able to access the eDisclosures because of the changed requirements. We will use commercially reasonable efforts to notify You before such requirements change. If You choose to withdraw Your consent upon notification of the change, You will be able to do so without penalty.<\/li><\/ul><p><u>HOW WE CAN REACH YOU<\/u><\/p><ul><li>You must promptly notify Us if there is a change in Your email address or in other information needed to contact You electronically. You can contact Us via telephone or mail.<\/li><li>We will not assume liability for non-receipt of notification of the availability of eDisclosures in the event Your email address on file is invalid; Your email or Internet service provider filters the notification as \"spam\" or \"junk mail\"; there is a malfunction in Your computer, browser, Internet service and\/or software; or for other reasons beyond Our control.<\/li><\/ul>`;
        break;
      case 'consentToMessages':
        confirmationText = `<h2>Your consent to receive messages from my lender for status updates on my loan</h2>
                    <p>By checking the \"I consent to receive messages from my lender for status updates on my loan\" checkbox, I\/we grant consent to receive messages from my lender for status updates on my loan.<\/p><p>SMS message service subscribers can: opt in to the SMS service in the Real-Finity Mortgage 1003 Web Application: opt out of the SMS service by replying with the keyword \"STOP\" to the message received; receive assistance with the SMS service by replying with the keyword \"HELP\" to the message received. You may receive up to five SMS messages per week, occasionally more. Standard message and data rates may apply.<\/p>`;
        break;
    }
    const viewTermsModal = this.dialog.open(ConfirmationDialogComponent,
      {
        data: {
          Confirmation: confirmationText,
          woCancelButton: true
        },
        width: '450px'
      }
    )
  }

  submit(): void {
    this.isLoadingSubmit = true
    this.loanAppService.createLoanApp().subscribe((response) => {
      this.isLoadingSubmit = false
      this.cdRef.detectChanges();
      this.contextService.clientToken = response.redirectUrl
      this.contextService.loanId = response.id
      this.goToStep(SubmitSteps.CreateLoanSuccess)
    })
  }
}
